/**
 * FILE: packages/supabase/src/services/roles.ts
 * PURPOSE: Supabase service layer — database access and business logic.
 * @deprecated Use domain-specific subpaths instead. This file will be removed in Phase 4.
 * AGENT_CONTEXT: All DB access goes through service layer. Call logActivity() on writes.
 * INVARIANTS: Soft-delete filter, phone PK protection, activity logging.
 * LAST_REVIEWED: 2026-05-07
 */


import { logActivity, type ActivityActor, withActivityActor } from '../internal/activity'
import type { Json } from '../types'
import type { DbClient } from "../dbClient";
import { defaultDbClient } from "../dbClient";
import type { TablesUpdate } from '../database.gen'

export const PERMISSION_DEFINITIONS = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    description: 'View the main dashboard with KPIs and quick actions',
    group: 'General',
  },
  {
    key: 'students',
    label: 'Students (Full)',
    description: 'View, search, and edit all student records. Includes enroll, extend, retake, and corrections.',
    group: 'Students',
  },
  {
    key: 'students_read',
    label: 'Students (Read Only)',
    description: 'View and search students. Cannot edit, delete, or modify any records.',
    group: 'Students',
  },
  {
    key: 'enroll',
    label: 'Enroll Students',
    description: 'Add new students to the system. Does not allow editing existing students.',
    group: 'Students',
  },
  {
    key: 'edit_student',
    label: 'Edit Students',
    description: 'Extend validity, process retakes, and correct student field values.',
    group: 'Students',
  },
  {
    key: 'placements',
    label: 'Placements',
    description: 'Manage jobs, candidates, applications, tracker, companies, and recruiters.',
    group: 'Placements',
  },
  {
    key: 'analytics',
    label: 'Analytics',
    description: 'View enrollment trends, revenue reports, validity expiry, and placement analytics.',
    group: 'Analytics',
  },
  {
    key: 'settings',
    label: 'Settings (Admin)',
    description: 'Manage staff accounts, custom roles, and system configuration. Super admin only.',
    group: 'Admin',
  },
  {
    key: 'activity',
    label: 'Activity Log',
    description: 'View the full audit trail of all system changes and actions.',
    group: 'Admin',
  },
  {
    key: 'support',
    label: 'Support',
    description: 'Manage community links and track onboarding email follow-ups. View student profiles read-only.',
    group: 'Support',
  },
  {
    key: 'operations',
    label: 'Operations',
    description: 'Access to bulk operations, data quality audits, and system health monitoring.',
    group: 'Admin',
  },
  {
    key: 'collaborate',
    label: 'Team Collaboration',
    description: 'Access team chat, collaboration threads, and shared workspaces.',
    group: 'General',
  },
  {
    key: 'content',
    label: 'Content Team',
    description: 'Access content management tools for doubt solving and Q&A workflows.',
    group: 'Content',
  },
] as const

const VALID_PERMISSION_KEYS = new Set<string>(PERMISSION_DEFINITIONS.map((d) => d.key))

export type Permission = (typeof PERMISSION_DEFINITIONS)[number]['key']

export interface CustomRole {
  id: string
  name: string
  description: string | null
  permissions: Permission[]
  created_at: string
  created_by: string | null
}

function assertPermissionsList(raw: unknown): Permission[] {
  if (!Array.isArray(raw)) return []
  const out: Permission[] = []
  for (const item of raw) {
    if (typeof item === 'string' && VALID_PERMISSION_KEYS.has(item)) {
      out.push(item as Permission)
    }
  }
  return out
}

/** Parse `custom_roles.permissions` from JSONB (array or double-encoded JSON string). */
export function permissionsFromJson(json: unknown): string[] {
  if (json == null) return []
  if (typeof json === 'string') {
    const t = json.trim()
    if (!t) return []
    try {
      const parsed = JSON.parse(t) as unknown
      return assertPermissionsList(parsed)
    } catch {
      return []
    }
  }
  return assertPermissionsList(json)
}

function rowToCustomRole(row: {
  id: string
  name: string
  description: string | null
  permissions: Json
  created_at: string | null
  created_by: string | null
}): CustomRole {
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    permissions: assertPermissionsList(row.permissions),
    created_at: row.created_at ?? '',
    created_by: row.created_by,
  }
}

export async function getCustomRoles(client: DbClient = defaultDbClient): Promise<CustomRole[]> {
  const { data, error } = await client
    .from('custom_roles')
    .select('id, name, description, permissions, created_at, created_by')
    .order('name', { ascending: true })

  if (error) throw error

  return (data ?? []).map((row) => {
    const raw = row.permissions
    const asList: unknown = Array.isArray(raw)
      ? raw
      : (() => {
          if (typeof raw !== 'string') return []
          try {
            return JSON.parse(raw) as unknown
          } catch {
            return []
          }
        })()

    const list = Array.isArray(asList) ? asList : []

    return {
      id: row.id,
      name: row.name,
      description: row.description,
      permissions: assertPermissionsList(list),
      created_at: row.created_at ?? '',
      created_by: row.created_by,
    }
  })
}

export async function createCustomRole(
  payload: { name: string; description?: string; permissions: Permission[] },
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<CustomRole> {
  const normalizedName = payload.name.trim().toLowerCase().replace(/\s+/g, '_')
  if (!normalizedName) throw new Error('Role name is required')
  const permissions = assertPermissionsList(payload.permissions)

  const { data, error } = await client
    .from('custom_roles')
    .insert({
      name: normalizedName,
      description: payload.description?.trim() || null,
      permissions: permissions as unknown as Json,
      created_by: actor.userId,
    })
    .select('id, name, description, permissions, created_at, created_by')
    .single()

  if (error) {
    console.error('createCustomRole error:', error)
    throw new Error(`Failed to create role: ${error.message}`)
  }

  const role = rowToCustomRole(data)
  await logActivity(
    withActivityActor(actor, {
      action: 'CREATE_CUSTOM_ROLE',
      entityType: 'custom_roles',
      entityId: role.id,
      newValues: { name: role.name, permissions: role.permissions },
    }), client
  ).catch((err) => {
    console.error('createCustomRole activity_log failed:', err)
  })

  return role
}

export async function updateCustomRole(
  id: string,
  changes: { name?: string; description?: string; permissions?: Permission[] },
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<CustomRole> {
  if (changes.permissions) {
    const permissions = assertPermissionsList(changes.permissions)
    if (permissions.length !== changes.permissions.length) {
      throw new Error('One or more permission keys are invalid')
    }
  }

  const { data: prevRow, error: prevErr } = await client.from('custom_roles').select('*').eq('id', id).maybeSingle()
  if (prevErr) throw prevErr

  const patch: Record<string, unknown> = {}
  if (changes.name !== undefined) patch.name = changes.name.trim()
  if (changes.description !== undefined) patch.description = changes.description?.trim() || null
  if (changes.permissions !== undefined) patch.permissions = assertPermissionsList(changes.permissions) as unknown as Json

  const { data, error } = await client.from('custom_roles').update(patch as unknown as TablesUpdate<'custom_roles'>).eq('id', id).select('*').single()
  if (error) throw error

  const role = rowToCustomRole(data)
  await logActivity(
    withActivityActor(actor, {
      action: 'UPDATE_CUSTOM_ROLE',
      entityType: 'custom_roles',
      entityId: id,
      oldValues: prevRow ? { name: prevRow.name, permissions: prevRow.permissions } : undefined,
      newValues: { name: role.name, permissions: role.permissions },
    }), client
  ).catch((err) => {
    console.error('updateCustomRole activity_log failed:', err)
  })
  return role
}

export async function deleteCustomRole(id: string, actor: ActivityActor, client: DbClient = defaultDbClient): Promise<void> {
  const { count, error: cErr } = await client
    .from('staff')
    .select('id', { count: 'exact', head: true })
    .eq('custom_role_id', id)
  if (cErr) throw cErr
  if ((count ?? 0) > 0) throw new Error('Cannot delete a role that is assigned to staff members')

  const { data: prevRow, error: prevErr } = await client.from('custom_roles').select('*').eq('id', id).maybeSingle()
  if (prevErr) throw prevErr

  const { error } = await client.from('custom_roles').delete().eq('id', id)
  if (error) throw error

  await logActivity(
    withActivityActor(actor, {
      action: 'DELETE_CUSTOM_ROLE',
      entityType: 'custom_roles',
      entityId: id,
      oldValues: prevRow ? { name: prevRow.name } : undefined,
    }), client
  ).catch((err) => {
    console.error('deleteCustomRole activity_log failed:', err)
  })
}
